import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import { isNative } from '@unifyapps/ui/utils/isNative';
import { produce } from 'immer';
import invariant from 'ts-invariant';
import type { StoreApi } from 'zustand/vanilla';
import { safeJsonParse, safeJsonStringify } from '../../../../../utils/json';
import { storage } from '../../../../../utils/storage';
import InterfaceHelper from '../../../InterfaceStore/helper/InterfaceHelper';
import type { GlobalAppStateStore, LocalStorageActionProps } from '../types';
import { validateStorageKey, validateStorageValue } from '../utils/validation';

const updateStorageAndState = (
  get: StoreApi<GlobalAppStateStore>['getState'],
  set: StoreApi<GlobalAppStateStore>['setState'],
  storageKey: string,
  key: string,
  value: unknown,
) => {
  const updatedLocalStorage = produce(get().localStorage, (draft) => {
    if (!draft.values) {
      draft.values = {};
    }

    if (value === undefined) {
      Reflect.deleteProperty(draft.values, key);
    } else {
      draft.values[key] = value;
    }
  });

  try {
    const existingData = storage.getItem(storageKey);
    const parsedData = existingData
      ? safeJsonParse<{ values?: Record<string, unknown> }>(existingData, { values: {} })
      : { values: {} };

    if (!parsedData.values) {
      parsedData.values = {};
    }

    parsedData.values[key] = value;

    const stringifiedData = safeJsonStringify(parsedData);
    if (stringifiedData === null) {
      throw new Error('Failed to stringify localStorage data');
    }
    storage.setItem(storageKey, stringifiedData);

    set((state) => ({
      ...state,
      localStorage: updatedLocalStorage,
    }));
  } catch (error) {
    console.error('Failed to update localStorage:', error);
    throw new Error('Failed to update localStorage');
  }
};

/**
 * Action creator for managing localStorage operations
 */
const getLocalStorageAction =
  (
    get: StoreApi<GlobalAppStateStore>['getState'],
    set: StoreApi<GlobalAppStateStore>['setState'],
  ) =>
  ({ operation, payload }: LocalStorageActionProps) => {
    const storageKey = InterfaceHelper.getLocalStorageKey(get().interfaceRecord.id);

    try {
      switch (operation) {
        case 'SET': {
          const { key, value } = payload;
          if (process.env.NODE_ENV === 'development' && !isNative()) {
            invariant(key, 'key is required in SET');
          }
          if (!key) break;
          validateStorageKey(key, 'SET');
          validateStorageValue(value);
          updateStorageAndState(get, set, storageKey, key, value);
          break;
        }
        case 'REMOVE': {
          const { key } = payload;
          if (process.env.NODE_ENV === 'development' && !isNative()) {
            invariant(key, 'key is required in REMOVE');
          }
          if (!key) break;
          validateStorageKey(key, 'REMOVE');
          updateStorageAndState(get, set, storageKey, key, undefined);
          break;
        }
        case 'CLEAR': {
          storage.removeItem(storageKey);
          set((state) => ({
            ...state,
            localStorage: { values: {} },
          }));
          break;
        }
        default:
          throw new Error(`Unknown localStorage operation: ${operation}`);
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('LocalStorage operation failed:', errorMessage);
      throw error;
    }
  };

export default getLocalStorageAction;

export const getInitializeLocalStorage = (
  interfaceRecord: InterfaceEntity | undefined,
): { values: Record<string, unknown> } => {
  if (!interfaceRecord) {
    return { values: {} };
  }

  try {
    const storageKey = InterfaceHelper.getLocalStorageKey(interfaceRecord.id);
    const value = storage.getItem(storageKey);
    if (value !== null) {
      try {
        const parsedData = safeJsonParse<{ values: Record<string, unknown> }>(value);
        return parsedData;
      } catch (error) {
        console.error('Failed to parse localStorage data, resetting to empty state', error);
        return { values: {} };
      }
    }
  } catch (error) {
    console.warn('Failed to initialize localStorage:', error);
  }
  return { values: {} };
};
